import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { LOGO_URL } from "@/lib/useReveal";

const NAV = [
    { to: "/admin", label: "Overview", end: true, icon: iconGrid },
    { to: "/admin/companies", label: "Companies", icon: iconBuilding },
    { to: "/admin/users", label: "Users", icon: iconUsers },
    { to: "/admin/roles", label: "Roles & Permissions", icon: iconShield },
    { to: "/admin/departments", label: "Departments", icon: iconLayers },
    { to: "/admin/agents", label: "AI Agents", icon: iconSpark },
    { to: "/admin/invites", label: "Invites", icon: iconMail },
    { to: "/admin/billing", label: "Billing & Plans", icon: iconCard },
    { to: "/admin/audit-logs", label: "Audit Logs", icon: iconClock },
    { to: "/admin/settings", label: "Settings", icon: iconGear },
];

export default function AdminLayout() {
    const { admin, loading, logout } = useAdminAuth();
    const location = useLocation();
    const nav = useNavigate();

    if (loading) {
        return (
            <div className="dot-grid min-h-screen flex items-center justify-center text-neutral-500">
                Loading admin…
            </div>
        );
    }
    if (!admin) {
        return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }

    const current =
        NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))) || NAV[0];

    async function onLogout() {
        await logout();
        nav("/admin/login", { replace: true });
    }

    return (
        <div className="dot-grid min-h-screen w-full flex text-black">
            {/* Sidebar */}
            <aside
                data-testid="admin-sidebar"
                className="hidden lg:flex flex-col w-[260px] shrink-0 sticky top-0 h-screen px-5 py-6 border-r border-black/[0.06] bg-white/70 backdrop-blur-xl"
            >
                <div className="flex items-center gap-2.5 px-1.5">
                    <img src={LOGO_URL} alt="" className="logo-blend h-9 w-9 object-contain" />
                    <div>
                        <div className="font-display text-[16px] font-medium leading-none">
                            CEO<span className="text-neutral-500">’s</span>{" "}
                            <span className="italic">GPT</span>
                        </div>
                        <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-violet-600 mt-1">
                            Admin
                        </div>
                    </div>
                </div>

                <nav className="mt-8 flex-1 space-y-0.5">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={`sidebar-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={({ isActive }) =>
                                `group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13.5px] transition-all ${
                                    isActive
                                        ? "bg-black text-white shadow-[0_10px_30px_-15px_rgba(139,92,246,0.55)]"
                                        : "text-neutral-700 hover:bg-black/[0.04]"
                                }`
                            }
                        >
                            <span className="inline-flex items-center justify-center h-6 w-6">
                                {n.icon()}
                            </span>
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <button
                    onClick={onLogout}
                    data-testid="sidebar-logout"
                    className="mt-6 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[13.5px] text-rose-600 hover:bg-rose-50 transition-colors"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M15 12H3M8 7L3 12l5 5M17 3h2a2 2 0 012 2v14a2 2 0 01-2 2h-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Logout
                </button>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Top bar */}
                <header
                    data-testid="admin-topbar"
                    className="sticky top-0 z-30 flex items-center justify-between gap-4 px-6 lg:px-10 h-16 border-b border-black/[0.06] bg-white/75 backdrop-blur-xl"
                >
                    <div className="flex items-center gap-3">
                        <span className="lg:hidden inline-flex items-center gap-2">
                            <img src={LOGO_URL} alt="" className="logo-blend h-7 w-7 object-contain" />
                            <span className="font-display text-[15px] font-medium">Admin</span>
                        </span>
                        <span className="hidden lg:block text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                            {current.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden md:flex items-center gap-2 text-[12px] text-neutral-500">
                            <span className="relative inline-flex h-1.5 w-1.5">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping"></span>
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                            </span>
                            All systems operational
                        </div>
                        <div className="flex items-center gap-2 rounded-full bg-white border border-black/10 px-2 py-1.5 pr-3">
                            <span className="h-6 w-6 rounded-full bg-black text-white text-[11px] font-semibold inline-flex items-center justify-center">
                                {(admin.email || "A").slice(0, 1).toUpperCase()}
                            </span>
                            <div className="leading-tight">
                                <div className="text-[12px] font-semibold text-black" data-testid="admin-profile-email">
                                    {admin.email}
                                </div>
                                <div className="text-[10px] text-neutral-500">{admin.role}</div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Content */}
                <main className="flex-1 px-6 lg:px-10 py-8 lg:py-10">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

/* ---------- Icons ---------- */
function iconGrid() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );
}
function iconBuilding() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M4 21V5a2 2 0 012-2h6a2 2 0 012 2v16M14 21V11a2 2 0 012-2h2a2 2 0 012 2v10M2 21h20M8 7h2M8 11h2M8 15h2M16 13h2M16 17h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}
function iconUsers() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
            <path d="M2 20c1.2-3.5 4-5.5 7-5.5s5.8 2 7 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M15 20c.5-2.4 2.3-4 4-4s3.5 1.6 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}
function iconShield() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function iconLayers() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 17l9 5 9-5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
function iconSpark() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6L12 3zM19 16l.7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7L19 16z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
function iconMail() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function iconCard() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 10h18M7 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}
function iconClock() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
            <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function iconGear() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
            <path d="M19 12a7 7 0 00-.1-1.2l2.1-1.6-2-3.4-2.5.8a7 7 0 00-2-1.2L14 3h-4l-.5 2.4c-.7.3-1.4.7-2 1.2L5 5.8l-2 3.4L5.1 10.8A7 7 0 005 12c0 .4 0 .8.1 1.2L3 14.8l2 3.4 2.5-.8c.6.5 1.3.9 2 1.2L10 21h4l.5-2.4c.7-.3 1.4-.7 2-1.2l2.5.8 2-3.4-2.1-1.6c.1-.4.1-.8.1-1.2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
